import { useEffect, useRef, useState } from 'react';
import { motion, useScroll, useSpring, useTransform, useReducedMotion } from 'framer-motion';

const BLUE = 'hsl(var(--primary))';
const PINK = 'hsl(var(--secondary))';
const S = { fill: 'none', strokeWidth: 4, strokeLinecap: 'round', strokeLinejoin: 'round' };

const Waves = () => (
    <svg viewBox="0 0 600 220" className="w-full h-auto">
        <path d="M10 150 C 120 40, 230 40, 330 110 S 520 200, 590 70" stroke={BLUE} {...S} />
        <path d="M10 200 C 140 110, 240 110, 330 140 S 500 150, 590 110" stroke={PINK} {...S} />
    </svg>
);
const ArcDot = () => (
    <svg viewBox="0 0 300 220" className="w-full h-auto">
        <path d="M20 210 A 140 140 0 0 1 280 150" stroke={BLUE} {...S} strokeWidth={3} />
        <circle cx="42" cy="132" r="10" fill={BLUE} />
    </svg>
);
const Capsule = () => (
    <svg viewBox="0 0 160 240" className="w-full h-auto">
        <g transform="rotate(-20 80 120)">
            <rect x="40" y="20" width="80" height="200" rx="40" stroke={BLUE} {...S} />
            <line x1="40" y1="120" x2="120" y2="120" stroke={BLUE} {...S} />
        </g>
    </svg>
);
const Dots = () => (
    <svg viewBox="0 0 160 220" className="w-full h-auto">
        {[[30, 30, 12], [110, 20, 14], [75, 60, 8], [40, 105, 9], [120, 120, 15], [25, 160, 13], [140, 175, 8], [85, 200, 11]].map(([x, y, r]) => (
            <circle key={`${x}-${y}`} cx={x} cy={y} r={r} fill={PINK} />
        ))}
    </svg>
);
const Bars = () => (
    <svg viewBox="0 0 140 110" className="w-full h-auto">
        {[[10, 15, 120], [10, 55, 90], [10, 95, 65]].map(([x, y, w]) => (
            <line key={y} x1={x} y1={y} x2={x + w} y2={y} stroke={PINK} {...S} strokeWidth={22} />
        ))}
    </svg>
);
const Stairs = () => (
    <svg viewBox="0 0 180 300" className="w-full h-auto">
        <path d="M10 290 V 220 H 70 V 160 H 120 V 100 H 160 V 20 H 175" stroke={BLUE} {...S} />
    </svg>
);
const Lines = () => (
    <svg viewBox="0 0 220 160" className="w-full h-auto">
        <line x1="10" y1="20" x2="200" y2="20" stroke={BLUE} {...S} />
        <line x1="10" y1="80" x2="120" y2="80" stroke={BLUE} {...S} />
        <line x1="10" y1="140" x2="200" y2="140" stroke={BLUE} {...S} />
    </svg>
);
const Slash = () => (
    <svg viewBox="0 0 170 260" className="w-full h-auto">
        <line x1="20" y1="20" x2="150" y2="180" stroke={PINK} {...S} strokeWidth={8} />
        <line x1="20" y1="245" x2="150" y2="245" stroke={BLUE} {...S} />
    </svg>
);
const Molecule = () => (
    <svg viewBox="0 0 220 200" className="w-full h-auto">
        <polygon points="80,40 120,40 140,75 120,110 80,110 60,75" stroke={BLUE} {...S} />
        <polygon points="120,110 160,110 180,145 160,180 120,180 100,145" stroke={BLUE} {...S} />
        <line x1="88" y1="52" x2="112" y2="52" stroke={BLUE} {...S} strokeWidth={3} />
        <line x1="60" y1="75" x2="25" y2="75" stroke={BLUE} {...S} />
        <line x1="140" y1="75" x2="175" y2="55" stroke={BLUE} {...S} />
        <circle cx="18" cy="75" r="8" fill={PINK} />
        <circle cx="182" cy="51" r="8" fill={PINK} />
    </svg>
);
const Flask = () => (
    <svg viewBox="0 0 160 220" className="w-full h-auto">
        <path d="M60 15 H100 M66 15 V80 L22 185 Q16 205 38 205 H122 Q144 205 138 185 L94 80 V15" stroke={BLUE} {...S} />
        <path d="M40 150 Q80 138 120 150" stroke={PINK} {...S} />
        <circle cx="72" cy="172" r="6" fill={PINK} />
        <circle cx="96" cy="182" r="4" fill={PINK} />
    </svg>
);
const Signal = () => (
    <svg viewBox="0 0 360 120" className="w-full h-auto">
        <path d="M5 60 H70 Q85 60 92 40 T112 60 H140 L152 15 L166 105 L178 60 H215 Q232 60 240 45 T262 60 H355" stroke={BLUE} {...S} />
        <path d="M5 95 H355" stroke={PINK} {...S} strokeWidth={2.5} strokeDasharray="2 12" />
    </svg>
);
const Scatter = () => (
    <svg viewBox="0 0 220 200" className="w-full h-auto">
        <path d="M20 15 V180 H210" stroke={BLUE} {...S} />
        <line x1="35" y1="160" x2="195" y2="45" stroke={PINK} {...S} strokeWidth={3} />
        {[[50, 150], [72, 128], [90, 140], [110, 104], [128, 112], [150, 80], [170, 72], [188, 50]].map(([x, y]) => (
            <circle key={x} cx={x} cy={y} r="6" fill={BLUE} />
        ))}
    </svg>
);
const Orbit = () => (
    <svg viewBox="0 0 220 220" className="w-full h-auto">
        <ellipse cx="110" cy="110" rx="95" ry="36" stroke={BLUE} {...S} strokeWidth={3} />
        <ellipse cx="110" cy="110" rx="95" ry="36" stroke={BLUE} {...S} strokeWidth={3} transform="rotate(60 110 110)" />
        <ellipse cx="110" cy="110" rx="95" ry="36" stroke={BLUE} {...S} strokeWidth={3} transform="rotate(-60 110 110)" />
        <circle cx="110" cy="110" r="12" fill={PINK} />
    </svg>
);
const Circle = () => (
    <svg viewBox="0 0 200 200" className="w-full h-auto">
        <circle cx="100" cy="100" r="94" stroke={PINK} {...S} strokeWidth={8} />
    </svg>
);
const Bars2 = () => (
    <svg viewBox="0 0 200 180" className="w-full h-auto">
        <path d="M15 165 H190" stroke={BLUE} {...S} />
        {[[35, 110], [75, 70], [115, 95], [155, 35]].map(([x, y], i) => (
            <rect key={x} x={x} y={y} width="26" height={160 - y} rx="4" fill={i === 3 ? PINK : 'none'} stroke={i === 3 ? PINK : BLUE} strokeWidth="4" />
        ))}
    </svg>
);
const Pie = () => (
    <svg viewBox="0 0 200 200" className="w-full h-auto">
        <circle cx="100" cy="100" r="80" stroke={BLUE} {...S} />
        <path d="M100 100 L100 20 A80 80 0 0 1 172 135 Z" fill={PINK} />
        <line x1="100" y1="100" x2="28" y2="135" stroke={BLUE} {...S} />
    </svg>
);
const Coin = () => (
    <svg viewBox="0 0 200 200" className="w-full h-auto">
        <circle cx="100" cy="100" r="78" stroke={BLUE} {...S} />
        <path d="M128 68 A42 42 0 1 0 128 132 M58 90 H112 M58 112 H112" stroke={PINK} {...S} strokeWidth={6} />
    </svg>
);
const Quote = () => (
    <svg viewBox="0 0 200 150" className="w-full h-auto">
        <path d="M30 120 Q20 60 75 30 M110 120 Q100 60 155 30" stroke={PINK} {...S} strokeWidth={10} />
        <circle cx="45" cy="115" r="16" fill={BLUE} />
        <circle cx="125" cy="115" r="16" fill={BLUE} />
    </svg>
);
const TextLines = () => (
    <svg viewBox="0 0 220 170" className="w-full h-auto">
        <rect x="10" y="10" width="200" height="150" rx="14" stroke={BLUE} {...S} />
        {[45, 75, 105].map((y, i) => <line key={y} x1="35" y1={y} x2={i === 2 ? 120 : 185} y2={y} stroke={i === 0 ? PINK : BLUE} {...S} />)}
    </svg>
);
const Question = () => (
    <svg viewBox="0 0 140 220" className="w-full h-auto">
        <path d="M25 65 Q25 20 70 20 Q115 20 115 62 Q115 90 85 105 Q70 113 70 140" stroke={BLUE} {...S} strokeWidth={6} />
        <circle cx="70" cy="185" r="12" fill={PINK} />
    </svg>
);
const Box = () => (
    <svg viewBox="0 0 220 210" className="w-full h-auto">
        <path d="M110 15 L200 60 V155 L110 200 L20 155 V60 Z M20 60 L110 105 L200 60 M110 105 V200" stroke={BLUE} {...S} />
        <path d="M65 37 L155 82" stroke={PINK} {...S} strokeWidth={6} />
    </svg>
);
const Check = () => (
    <svg viewBox="0 0 200 200" className="w-full h-auto">
        <circle cx="100" cy="100" r="80" stroke={BLUE} {...S} />
        <path d="M62 102 L90 130 L142 74" stroke={PINK} {...S} strokeWidth={8} />
    </svg>
);
const Ring = () => (
    <svg viewBox="0 0 200 200" className="w-full h-auto">
        <circle cx="100" cy="100" r="80" stroke={BLUE} {...S} />
    </svg>
);

// Side-margin slots so shapes never sit behind running text on content pages.
const SLOTS = [
    { top: '2%', left: '3%' }, { top: '8%', left: '86%' }, { top: '22%', left: '-2%' }, { top: '34%', left: '88%' },
    { top: '48%', left: '2%' }, { top: '60%', left: '86%' }, { top: '74%', left: '3%' }, { top: '88%', left: '88%' },
];
const margin = (list) => list.map(([C, w, extra = {}], i) => ({ C, w, speed: 0.6 + (i % 3) * 0.08, rot: (i % 2 ? -1 : 1) * 6, ...SLOTS[i], ...extra }));

// Fewer shapes, spread across the width; central ones softened so text stays readable.
const SPREAD = [
    { top: '5%', left: '68%' }, { top: '24%', left: '4%' }, { top: '44%', left: '52%', op: 0.45 },
    { top: '64%', left: '86%' }, { top: '84%', left: '24%', op: 0.45 },
];
const spread = (list) => list.map(([C, w, extra = {}], i) => ({ C, w, speed: 0.6 + (i % 3) * 0.08, rot: (i % 2 ? -1 : 1) * 6, ...SPREAD[i], ...extra }));

// top/left in % of the flow block; speed = fraction of scroll the shape lags behind.
const SHAPES = [
    { C: Waves, top: '4%', left: '28%', w: 'w-[80vw] lg:w-[42vw]', speed: 0.65, rot: 4 },
    { C: Circle, top: '13%', left: '78%', w: 'w-[30vw] lg:w-[14vw]', speed: 0.8, rot: 0, op: 0.85 },
    { C: Dots, top: '31%', left: '50%', w: 'w-[16vw] lg:w-[6vw]', speed: 0.75, rot: 0 },
    { C: Capsule, top: '44%', left: '10%', w: 'w-[20vw] lg:w-[8vw]', speed: 0.6, rot: 12 },
    { C: ArcDot, top: '58%', left: '56%', w: 'w-[40vw] lg:w-[18vw]', speed: 0.7, rot: -8 },
    { C: Bars, top: '74%', left: '80%', w: 'w-[16vw] lg:w-[7vw]', speed: 0.65, rot: 0 },
];

const SHAPES_BOTTOM = [
    { C: Stairs, top: '3%', left: '70%', w: 'w-[18vw] lg:w-[7vw]', speed: 0.7, rot: 0 },
    { C: Circle, top: '10%', left: '6%', w: 'w-[28vw] lg:w-[12vw]', speed: 0.8, rot: 0, op: 0.7 },
    { C: Lines, top: '22%', left: '46%', w: 'w-[24vw] lg:w-[10vw]', speed: 0.65, rot: 0 },
    { C: Waves, top: '33%', left: '52%', w: 'w-[80vw] lg:w-[40vw]', speed: 0.6, rot: 5 },
    { C: Slash, top: '46%', left: '12%', w: 'w-[18vw] lg:w-[7vw]', speed: 0.75, rot: 0 },
    { C: Dots, top: '58%', left: '78%', w: 'w-[16vw] lg:w-[6vw]', speed: 0.7, rot: 0 },
    { C: ArcDot, top: '70%', left: '30%', w: 'w-[40vw] lg:w-[18vw]', speed: 0.65, rot: 6 },
    { C: Capsule, top: '83%', left: '72%', w: 'w-[18vw] lg:w-[7vw]', speed: 0.6, rot: -14 },
    { C: Ring, top: '92%', left: '40%', w: 'w-[26vw] lg:w-[11vw]', speed: 0.8, rot: 0, op: 0.6 },
];

// Science page: shapes stay in the side margins so they never sit behind running text.
const SHAPES_SCIENCE = [
    { C: Molecule, top: '1%', left: '4%', w: 'w-[12vw]', speed: 0.7, rot: 6 },
    { C: Orbit, top: '3%', left: '72%', w: 'w-[13vw]', speed: 0.8, rot: 10 },
    { C: Signal, top: '13%', left: '-6%', w: 'w-[24vw]', speed: 0.65, rot: 0 },
    { C: Flask, top: '26%', left: '89%', w: 'w-[6vw]', speed: 0.7, rot: 8 },
    { C: Scatter, top: '40%', left: '3%', w: 'w-[8vw]', speed: 0.75, rot: 0 },
    { C: Molecule, top: '54%', left: '87%', w: 'w-[9vw]', speed: 0.6, rot: -10 },
    { C: Signal, top: '68%', left: '84%', w: 'w-[18vw]', speed: 0.7, rot: 0 },
    { C: Orbit, top: '80%', left: '2%', w: 'w-[9vw]', speed: 0.8, rot: -8 },
    { C: Flask, top: '92%', left: '90%', w: 'w-[5vw]', speed: 0.7, rot: -6 },
];

// Mobile layout: bigger shapes tucked half off the screen edges, alternating sides, so they peek out beside the text.
const mobilize = (list) => list.map((s, i) => {
    const vw = parseFloat((s.w.match(/w-\[(\d+(?:\.\d+)?)vw\]/) || [])[1] || 10);
    const w = Math.min(70, Math.round(vw * 3));
    return { ...s, w: '', width: `${w}vw`, left: i % 2 ? `calc(100% - ${Math.round(w * 0.55)}vw)` : `-${Math.round(w * 0.4)}vw`, op: Math.min(s.op ?? 1, 0.85) };
});

const VARIANTS = {
    top: SHAPES,
    bottom: SHAPES_BOTTOM,
    science: SHAPES_SCIENCE,
    business: spread([[Bars2, 'w-[9vw]'], [Pie, 'w-[8vw]'], [Coin, 'w-[8vw]'], [Scatter, 'w-[8vw]'], [Ring, 'w-[8vw]', { op: 0.6 }]]),
    blog: spread([[TextLines, 'w-[10vw]'], [Quote, 'w-[7vw]'], [Waves, 'w-[24vw]'], [Dots, 'w-[5vw]'], [Ring, 'w-[8vw]', { op: 0.6 }]]),
    about: spread([[Waves, 'w-[22vw]'], [Capsule, 'w-[6vw]'], [ArcDot, 'w-[14vw]'], [Ring, 'w-[8vw]'], [Stairs, 'w-[6vw]']]),
    faq: spread([[Question, 'w-[6vw]'], [Ring, 'w-[8vw]'], [ArcDot, 'w-[14vw]'], [Question, 'w-[5vw]'], [Dots, 'w-[5vw]']]),
    neutral: margin([[Waves, 'w-[24vw]'], [Ring, 'w-[10vw]'], [Dots, 'w-[5vw]'], [Ring, 'w-[9vw]'], [Lines, 'w-[8vw]'], [Waves, 'w-[20vw]'], [Ring, 'w-[7vw]'], [Dots, 'w-[5vw]']]),
    order: margin([[Box, 'w-[9vw]'], [Check, 'w-[7vw]'], [Waves, 'w-[20vw]'], [Dots, 'w-[5vw]'], [Ring, 'w-[8vw]']]),
};

function Shape({ shape, raw, smooth, height, offset, max, vh, reduce, touch }) {
    // Anchor: the scroll position at which this shape sits at its nominal spot (mid-viewport).
    const anchor = offset + (parseFloat(shape.top) / 100) * height - vh / 2;
    // Screen position depends only on the smoothed scroll, so wheel "clicks" glide instead of jump.
    const y = useTransform([raw, smooth], ([r, s]) => (reduce ? 0 : r - s + (s - anchor) * shape.speed));
    const rotate = useTransform(smooth, (v) => (reduce ? 0 : ((v - anchor) / 2000) * shape.rot));
    const { C } = shape;
    if (touch) {
        // Touch: compositor-driven CSS scroll timeline on the page scroller; same formula as desktop, (scroll - anchor) * speed.
        return (
            <div
                className={`absolute flow-shape-touch ${shape.w}`}
                style={{
                    top: shape.top,
                    left: shape.left,
                    width: shape.width,
                    opacity: shape.op ?? 1,
                    '--from': `${(-anchor * shape.speed).toFixed(1)}px`,
                    '--to': `${((max - anchor) * shape.speed).toFixed(1)}px`,
                }}
            >
                <C />
            </div>
        );
    }
    return (
        <motion.div
            className={`absolute ${shape.w}`}
            style={{ top: shape.top, left: shape.left, width: shape.width, y, rotate, opacity: shape.op ?? 1 }}
        >
            <C />
        </motion.div>
    );
}

export function FlowBackground({ variant = 'top' }) {
    const [mobile] = useState(() => typeof window !== 'undefined' && (window.matchMedia('(pointer: coarse)').matches || window.innerWidth < 1024));
    const { scrollY: raw } = useScroll();
    // Critically damped spring: glides between wheel "clicks" without overshoot.
    const smooth = useSpring(raw, { stiffness: 70, damping: 22, mass: 0.6, restDelta: 0.5 });
    const reduceMotion = useReducedMotion();
    // Touch scrolling runs off the main thread, so JS parallax always lags and stutters there; keep shapes static.
    const [touch] = useState(() => typeof window !== 'undefined' && window.matchMedia('(pointer: coarse)').matches);
    const reduce = reduceMotion || touch;
    const ref = useRef(null);
    const [box, setBox] = useState({ height: 0, offset: 0 });
    useEffect(() => {
        // Large-viewport height, captured once so the iPhone address bar showing/hiding never shifts shapes.
        const probe = document.createElement('div');
        probe.style.cssText = 'position:absolute;top:0;height:100lvh;width:0;visibility:hidden';
        document.body.appendChild(probe);
        const vh = probe.offsetHeight || window.innerHeight;
        probe.remove();
        let raf = 0;
        let lastW = -1;
        let lastDocH = -1;
        const measure = () => {
            cancelAnimationFrame(raf);
            raf = requestAnimationFrame(() => {
                const el = ref.current;
                if (!el) return;
                const docH = document.documentElement.scrollHeight;
                if (window.innerWidth === lastW && Math.abs(docH - lastDocH) <= 100) return;
                lastW = window.innerWidth;
                lastDocH = docH;
                setBox({
                    height: el.offsetHeight,
                    offset: el.getBoundingClientRect().top + window.scrollY,
                    max: Math.max(1, docH - vh),
                    vh,
                });
            });
        };
        measure();
        const ro = new ResizeObserver(measure);
        ro.observe(document.body);
        window.addEventListener('resize', measure);
        return () => { cancelAnimationFrame(raf); ro.disconnect(); window.removeEventListener('resize', measure); };
    }, []);
    const shapes = mobile && variant !== 'top' && variant !== 'bottom' ? mobilize(VARIANTS[variant]) : VARIANTS[variant];
    const mask = variant === 'top'
        ? '[mask-image:linear-gradient(to_bottom,#000_0%,#000_88%,transparent_100%)]'
        : '[mask-image:linear-gradient(to_bottom,transparent_0%,#000_6%,#000_94%,transparent_100%)]';
    const hide = '';
    // Masks force WebKit to repaint the layer every frame, which kills compositor-smooth motion on phones.
    const maskCls = mobile ? '' : mask;
    return (
        <div ref={ref} aria-hidden="true" className={`absolute inset-0 -z-10 overflow-clip pointer-events-none ${maskCls} ${hide}`} data-testid={`flow-background${variant === 'top' ? '' : `-${variant}`}`}>
            {box.height > 0 && shapes.map((s, i) => (
                <Shape key={i} shape={s} raw={raw} smooth={smooth} height={box.height} offset={box.offset} max={box.max} vh={box.vh} reduce={reduce} touch={touch && !reduceMotion} />
            ))}
        </div>
    );
}
